import {Dialog, Transition} from "@headlessui/react";
import {Fragment, type HTMLAttributes} from "react";
import {useForm} from "react-hook-form";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {toast} from "react-toastify";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from 'yup';
import {ReferenceApi, TerminalApi} from "../../../api";
import {ReferenceType} from "../../../model/ReferenceType";
import {useMerchant} from "../../../hooks";

const schema = yup.object({
    name: yup.string().required(),
    flowTypeId: yup.string().required(),
    currencyId: yup.string().required(),
});

export const TerminalDialog = ({
                                   isOpen,
                                   onClose
                               }: {
    isOpen: boolean;
    onClose: () => void;
} & HTMLAttributes<HTMLDivElement>) => {
    const queryClient = useQueryClient();
    const merchant = useMerchant();
    const id = merchant && merchant.id;

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema),
    });

    const {data: currencyList = []} = useQuery({
        queryKey: [ReferenceApi.KEY, 'currency'],
        queryFn: ReferenceApi.currency,
    });

    const {data: flowTypeList = []} = useQuery({
        queryKey: [ReferenceApi.KEY, ReferenceType.FLOW_TYPE],
        queryFn: () => ReferenceApi.getById(ReferenceType.FLOW_TYPE),
    });

    const merchantCreateMutation = useMutation({
        mutationFn: (data: yup.InferType<typeof schema>) => TerminalApi.createTerminal(id as unknown as string, data),
        onSuccess: () => {
            queryClient.refetchQueries([TerminalApi.KEY]).then(() => {
                toast.success('Terminal created');
            });
        },
    });

    const onSubmit = async (data: yup.InferType<typeof schema>) => {
        merchantCreateMutation.mutate(data)
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <div className="bg-white px-8 py-10 rounded-3xl w-[768px]">
                                <div className="grid gap-8">
                                    <div className="grid grid-cols-[100px_1fr] items-center">
                                        <span className="text-left">Name:</span>
                                        <input
                                            className="input"
                                            {...register("name")}
                                            type="text"
                                        />
                                    </div>
                                    {errors.name && typeof errors.name.message === 'string' && (
                                        <span className="text-rose-700">{errors.name.message}</span>
                                    )}
                                    <div className="grid grid-cols-[100px_1fr] items-center">
                                        <span className="text-left">Flow type:</span>
                                        <select
                                            {...register('flowTypeId', {
                                                required: true,
                                                valueAsNumber: true,
                                            })}
                                            className="input appearance-none w-full"
                                        >
                                            {flowTypeList.map((option) => (
                                                <option key={option.id} value={option.id}>
                                                    {option.value.replace('flow.type.', '')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.flowTypeId && typeof errors.flowTypeId.message === 'string' && (
                                        <span className="text-rose-700">{errors.flowTypeId.message}</span>
                                    )}
                                    <div className="grid grid-cols-[100px_1fr] items-center">
                                        <span className="text-left">Currency:</span>
                                        <select
                                            {...register('currencyId', {
                                                required: true,
                                                valueAsNumber: true,
                                            })}
                                            className="input appearance-none w-full"
                                        >
                                            {currencyList.map((option) => (
                                                <option key={option.id} value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.currencyId && typeof errors.currencyId.message === 'string' && (
                                        <span className="text-rose-700">{errors.currencyId.message}</span>
                                    )}
                                    <button className="button button--block button--primary"
                                            disabled={merchantCreateMutation.isLoading}
                                            onClick={handleSubmit(onSubmit)}>Save
                                    </button>
                                </div>
                            </div>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}